import { Fragment, useEffect, useMemo, useState } from "react"

import { LoadingOutlined } from "@ant-design/icons"
import {
  Alert,
  App,
  Checkbox,
  Flex,
  Form,
  Input,
  Select,
  Space,
  Spin,
  Tooltip,
  Typography
} from "antd"
import classname from "classname"
import { useTranslation } from "react-i18next"
import { useDispatch, useSelector } from "react-redux"
import { useLocation } from "react-router"

import { userActions } from "../../Containers/User/redux/user.slice"
import {
  checkMaverickDba,
  connectMaverickTerminals,
  getPaymentProcessors
} from "../../Containers/User/redux/user.thunks"
import { useIncognito } from "../../hooks/useIncognito"
import Icon from "../../Icon"
import { modalConfig } from "../../theme"
import AppUtils from "../../Utils/utils"
import ButtonComponent from "../Button"
import InputComponent from "../Input"

import "./Form.less"

const { Text } = Typography

const spaceVertical = ["chargebee", "next"]

function ConnectMaverick({
  fields,
  onClose,
  error,
  extraBtn,
  disabled = false,
  service_name,
  merchant_id,
  isMobile
}) {
  const { pathname } = useLocation()
  const { t } = useTranslation()
  const { modal } = App.useApp()
  const incognito = useIncognito()
  const [step, setStep] = useState(1)
  const [formData, setFormData] = useState({})
  const [form] = Form.useForm()
  const dispatch = useDispatch()
  const { maverickDba, maverickDbaFetching, maverickDbaError } = useSelector((state) => state.user)
  const [selectedTerminals, setSelectedTerminals] = useState([])

  const options = useMemo(
    () =>
      maverickDba?.terminals?.map((item) => ({
        label: item.name,
        value: item.id
      })) || [],
    [maverickDba]
  )

  const noTerminalsAvailable = options.length === 0

  useEffect(() => {
    const subscription = (value) => {
      setSelectedTerminals(value || [])
    }
    const unsubscribe = form.getFieldValue("terminal", subscription)
    return unsubscribe
  }, [form])

  useEffect(() => {
    if (maverickDba?.status === "ok") {
      setStep(2)
      if (maverickDba.default_selected_terminal_ids) {
        const selected = maverickDba.terminals
          .filter((t) => maverickDba.default_selected_terminal_ids.includes(t.id))
          .map((t) => ({
            value: t.id,
            label: t.name
          }))

        form.setFieldsValue({ terminal: selected })
        setSelectedTerminals(selected)
      } else {
        form.setFieldsValue({ terminal: [] })
        setSelectedTerminals([])
      }
    }
  }, [maverickDba, form])

  useEffect(() => {
    form.resetFields()
    setStep(1)
    setFormData({})
    setSelectedTerminals([])

    dispatch(userActions.checkMaverickDbaReset())
  }, [])

  const onFinish = async (value) => {
    if (step === 1) {
      setFormData(value)

      dispatch(
        checkMaverickDba({
          merchant_id: merchant_id,
          dba_id: value.dba_id
        })
      )
    } else {
      const data = { ...formData, ...value }

      const terminal_ids = (data.terminal || []).map((t) => t.value).join(",")

      try {
        await dispatch(
          connectMaverickTerminals({ merchant_id, dba_id: data.dba_id, terminal_ids })
        ).unwrap()
      } catch {
        // The thunk already raised the global error notification; just don't refetch.
        return
      }

      dispatch(
        getPaymentProcessors({
          integrationsPage: true,
          merchant_id: merchant_id,
          return_url: process.env.REACT_APP_BASE_URI + pathname
        })
      )
    }
  }
  const handleClose = () => {
    modal.confirm({
      ...modalConfig("exclamation"),
      centered: !isMobile,
      title: "Are you sure you want to exit?",
      content:
        "The Maverick Payments account connection will not be saved. You will be able to connect Maverick from scratch again.",
      okText: "Exit",
      cancelText: "Stay",
      okButtonProps: {
        ...modalConfig("exclamation")?.okButtonProps,
        className: "maverick-exit-btn"
      },
      onOk() {
        clearFormData()
        dispatch(userActions.checkMaverickDbaReset())
        setStep(1)
        onClose()
      }
    })
  }

  function clearFormData() {
    form.resetFields()
  }

  function handleClearError(e, field) {
    form.setFields([
      {
        name: field,
        errors: []
      }
    ])
  }

  function onFocusHandler(e) {
    handleClearError(e, e.target.name)
  }

  const validateNotOnlyWhitespace = (_, value) => {
    if (!value || !value.trim()) {
      return Promise.reject(new Error(t("Required fields were not filled.")))
    }
    return Promise.resolve()
  }
  const validateOnlyNumbers = (_, value) => {
    if (!value) {
      return Promise.reject(new Error(""))
    }

    if (!/^\d+$/.test(value)) {
      return Promise.reject(new Error(t("DBA can only contain numbers.")))
    }

    return Promise.resolve()
  }

  const allSelected = selectedTerminals.length === options.length && options.length > 0

  return (
    <Form
      className={"with-default-error"}
      onFinish={onFinish}
      disabled={disabled}
      validateTrigger="onSubmit"
      layout="vertical"
      form={form}
      requiredMark={true}
    >
      {step === 1 &&
        fields.map((field) => (
          <Fragment key={field.id || field}>
            <Form.Item
              //key={field}
              label={field?.label || field}
              name={field?.id || field}
              rules={[
                { required: true, message: "" },
                { validator: validateNotOnlyWhitespace },
                { validator: validateOnlyNumbers },
                ...(field?.rules || [])
              ]}
              style={{ marginBottom: "16px" }}
              normalize={(value) => value?.trim() || ""}
            >
              {field?.["is_secure"] || incognito ? (
                <Input.Password
                  name={field?.id || field}
                  placeholder={field?.placeholder || null}
                  autoComplete="new-password"
                  onFocus={onFocusHandler}
                  visibilityToggle={!!field?.["is_secure"]}
                />
              ) : (
                <InputComponent
                  name={field?.id || field}
                  placeholder={field?.placeholder || null}
                  autoComplete="new-password"
                  onFocus={onFocusHandler}
                />
              )}
            </Form.Item>

            {!error && (
              <Form.Item
                noStyle
                shouldUpdate={false}
                dependencies={null}
                rules={{ required: true, message: "" }}
              >
                <div style={{ margin: "-8px 0 16px" }}>
                  <Space size={4}>
                    <Text>
                      <Space
                        size={0}
                        direction={spaceVertical.includes(service_name) && "vertical"}
                      >
                        <Text>{AppUtils.makeBoldText(field?.description?.text, "secondary")}</Text>
                        <Text className="text-with-icon">
                          {field?.description?.tooltip ? (
                            <Tooltip
                              placement={isMobile ? "top" : "topLeft"}
                              arrow={false}
                              title={field?.description?.tooltip}
                            >
                              <div style={{ marginLeft: "2px" }}>
                                <Icon type="circle_info" style={{ color: "#7A7A83" }} />
                              </div>
                            </Tooltip>
                          ) : null}
                        </Text>
                      </Space>
                    </Text>
                  </Space>
                </div>
              </Form.Item>
            )}
          </Fragment>
        ))}
      {step === 2 && (
        <Form.Item
          name="terminal"
          label="Select terminals"
          required
          rules={[
            {
              validator: (_, value) => {
                if (!value || value.length === 0) {
                  return Promise.reject(new Error("Select at least 1 terminal"))
                }
                return Promise.resolve()
              }
            }
          ]}
        >
          <Select
            style={{ paddingBottom: 12 }}
            mode="multiple"
            labelInValue
            placeholder="All terminals"
            disabled={noTerminalsAvailable}
            value={allSelected ? [{ value: "all", label: "All terminals" }] : selectedTerminals}
            onChange={(val) => {
              const filteredVal = val.filter((v) => v.value !== "all")

              if (filteredVal.length === options.length && options.length > 0) {
                const allOptions = [...options]
                form.setFieldsValue({ terminal: allOptions })
                setSelectedTerminals(allOptions)
              } else {
                form.setFieldsValue({ terminal: filteredVal })
                setSelectedTerminals(filteredVal)
              }
            }}
            maxTagCount={allSelected ? 0 : 2}
            maxTagPlaceholder={(omittedValues) => {
              if (allSelected) return "All terminals"
              return `+${omittedValues.length}`
            }}
            dropdownRender={() => {
              const selectedValues = selectedTerminals.map((v) => v.value)
              const isAllChecked = selectedValues.length === options.length && options.length > 0

              return (
                <div style={{ padding: 8 }}>
                  <Checkbox
                    checked={isAllChecked}
                    onChange={(e) => {
                      if (e.target.checked) {
                        const allOptions = [...options]
                        form.setFieldsValue({ terminal: allOptions })
                        setSelectedTerminals(allOptions)
                      } else {
                        form.setFieldsValue({ terminal: [] })
                        setSelectedTerminals([])
                      }
                    }}
                  >
                    All terminals
                  </Checkbox>

                  <div
                    style={{
                      marginTop: 6,
                      display: "flex",
                      flexDirection: "column",
                      gap: 5
                    }}
                  >
                    {options.map((opt) => (
                      <div key={opt.value}>
                        <Checkbox
                          checked={selectedValues.includes(opt.value)}
                          onChange={(e) => {
                            let newValues

                            if (e.target.checked) {
                              newValues = [...selectedTerminals, opt]
                            } else {
                              newValues = selectedTerminals.filter((v) => v.value !== opt.value)
                            }

                            form.setFieldsValue({ terminal: newValues })
                            setSelectedTerminals(newValues)
                          }}
                        >
                          {opt.label}
                        </Checkbox>
                      </div>
                    ))}
                  </div>
                </div>
              )
            }}
          />
          {step === 2 && noTerminalsAvailable && (
            <Alert
              type="error"
              message={
                <Text strong>All terminals are already connected to other business accounts</Text>
              }
              description={
                <Text>To connect this integration, at least one terminal must be available.</Text>
              }
              showIcon
              style={{ marginBottom: 16 }}
            />
          )}
        </Form.Item>
      )}
      {maverickDbaError && (
        <Alert
          type="error"
          message={<Text strong>{t("Payment processor connection failed")}</Text>}
          description={<Text>{maverickDbaError}</Text>}
          showIcon
          icon={<Icon type="alert_triangle_error" />}
          style={{ marginBottom: 16 }}
        />
      )}
      {isMobile ? (
        <div className={"connect-payment-processor-mobile-buttons"}>
          {step === 1 && extraBtn}
          <ButtonComponent size="large" htmlType="button" onClick={handleClose}>
            {t("Cancel")}
          </ButtonComponent>
          <ButtonComponent
            type="primary"
            size="large"
            htmlType="submit"
            disabled={step === 2 && noTerminalsAvailable}
            className={classname("connect-btn", { step1: step === 1 })}
          >
            {disabled || maverickDbaFetching ? (
              <Spin
                size="large"
                style={{
                  width: 50,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center"
                }}
                indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}
              />
            ) : (
              t("Connect")
            )}
          </ButtonComponent>
        </div>
      ) : (
        <Flex gap={16} justify="end" style={{ padding: "0 3px 3px 0" }}>
          {step === 1 && extraBtn}
          <ButtonComponent size="large" htmlType="button" onClick={handleClose}>
            {t("Cancel")}
          </ButtonComponent>

          <ButtonComponent
            type="primary"
            size="large"
            htmlType="submit"
            disabled={step === 2 && noTerminalsAvailable}
          >
            {disabled || maverickDbaFetching ? (
              <Spin
                size="large"
                style={{
                  width: 50,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center"
                }}
                indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}
              />
            ) : (
              t("Connect")
            )}
          </ButtonComponent>
        </Flex>
      )}
    </Form>
  )
}

export default ConnectMaverick
